import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Tooltip,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Card,
    CardContent,
    Stack,
} from "@mui/material";
import { format } from "date-fns";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SpeedIcon from '@mui/icons-material/Speed';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import CategoryIcon from '@mui/icons-material/Category';

import { useVehiculoDetalle } from "../../hooks/useVehicles";
import { useDocuments } from "../../hooks/useDocuments";
import DocumentsForm from "./DocumentsForm";

import { useAuthContext } from "../../context/AuthProvider";

const estadoColor = {
    VIGENTE: "success",
    "POR VENCER": "warning",
    VENCIDO: "error",
    "NO REGISTRADO": "default",
};

const getEstadoDocumento = (doc) => (!doc ? "NO REGISTRADO" : doc.docest || "NO REGISTRADO");

const OWNERSHIP_MAP = {
    OWNED: { label: "Propio", color: "success" },
    CUSTOMER: { label: "Cliente", color: "info" },
    LEASED: { label: "Leasing", color: "warning" },
    MANAGED: { label: "Administrado", color: "secondary" },
    THIRD_PARTY: { label: "Tercero", color: "default" },
};

const VehicleDetailDashboard = () => {
    const { user } = useAuthContext();
    const { id } = useParams();
    const [selectedProject, setSelectedProject] = useState(null);
    const [documentosRequeridos, setDocumentosRequeridos] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [openPreview, setOpenPreview] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [editDocument, setEditDocument] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [formDefaultValues, setFormDefaultValues] = useState({});

    const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
    const operationID = Number(process.env.REACT_APP_ROL_OPERACIONES_ID);

    const { createMutation, updateMutation } = useDocuments();

    const [formConfig, setFormConfig] = useState({
        forcedAssociationType: "vehiculo",
        lockAssociationType: false,
        lockDocumentType: false,
        lockVehicle: false,
        lockEmployee: false,
    });

    const {
        vehiculo,
        proyectos,
        documentosVehiculo,
        isLoading,
        error,
        fetchProjectDocuments,
        refetchDocumentosVehiculo,
    } = useVehiculoDetalle(id);

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleSubmitDocument = (data) => {
        if (!user) return showAlert("Debe iniciar sesión para realizar esta acción", "error");

        const isEditing = data.get("docid");

        if (isEditing) {
            data.append("updbyid", user.EmpID);
            updateMutation.mutate({ id: data.get("docid"), data }, {
                onSuccess: (result) => {
                    if (result?.error) showAlert(result.error, "error");
                    else {
                        showAlert("Documento actualizado con éxito");
                        setOpenForm(false);
                        setEditDocument(null);
                        refetchDocumentosVehiculo();
                    }
                },
                onError: (error) => {
                    showAlert(error.response?.data?.error || "Error al actualizar el documento", "error");
                },
            });
        } else {
            data.append("crebyid", user.EmpID);
            createMutation.mutate(data, {
                onSuccess: (result) => {
                    if (result?.error) {
                        showAlert(result.error, "error");
                        setOpenForm(false);
                    } else {
                        showAlert("Documento creado con éxito");
                        setOpenForm(false);
                        refetchDocumentosVehiculo();
                    }
                },
                onError: (error) => {
                    showAlert(error.response?.data?.error || "Error al crear el documento", "error");
                },
            });
        }
    };

    const handleOpenPreview = (ruta) => {
        const fullUrl = `${process.env.REACT_APP_API_URL}/documents/file/${ruta}`;
        setPreviewUrl(fullUrl);
        setOpenPreview(true);
    };

    const handleClosePreview = () => {
        setOpenPreview(false);
        setPreviewUrl(null);
    };

    const handleOpenProject = async (proyecto) => {
        const docs = await fetchProjectDocuments(proyecto.proid);
        const vehiculoDocs = docs.filter((doc) => doc.reqent === "VEHICULO");
        setSelectedProject(proyecto);
        setDocumentosRequeridos(vehiculoDocs);
    };

    const handleOpenForm = (data = {}, options = {}) => {
        const isEditing = !!data.docid;
        const enrichedData = { ...data, vehid: parseInt(id, 10), associationType: "vehiculo" };

        if (!isEditing) {
            delete enrichedData.docid;
            delete enrichedData.docrut;
            delete enrichedData.docest;
            delete enrichedData.docfecemi;
            delete enrichedData.docfecven;
        }

        setFormDefaultValues(enrichedData);
        setFormConfig({
            forcedAssociationType: "vehiculo",
            lockAssociationType: true,
            lockDocumentType: !!options.lockDocumentType,
            lockVehicle: true,
            lockEmployee: true,
        });
        setOpenForm(true);
    };

    if (isLoading) return <Typography sx={{ p: 3 }}>Cargando datos...</Typography>;
    if (error) return <Typography color="error" sx={{ p: 3 }}>Error al cargar información</Typography>;

    const ownership = OWNERSHIP_MAP[vehiculo.ownershiptype] || { label: vehiculo.ownershiptype || "N/R", color: "default" };

    return (
        <Box sx={{ p: 3, bgcolor: '#f9f9f9', minHeight: '100vh' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>Detalle del Vehículo</Typography>

            {/* TARJETAS DE INFORMACIÓN GENERAL */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Identificación</Typography>
                            <Typography variant="h6" fontWeight={700} gutterBottom>{vehiculo.vehmat}</Typography>
                            <Typography variant="body2" color="text.secondary">{vehiculo.vehmar} {vehiculo.vehmod}</Typography>

                            <Stack direction="row" spacing={1} mt={2}>
                                <Chip label={vehiculo.vehest} color={vehiculo.vehest === 'ACTIVO' ? 'success' : 'warning'} size="small" variant="outlined" />
                                <Chip icon={<CategoryIcon />} label={`Tenencia: ${ownership.label}`} color={ownership.color} size="small" variant="outlined" />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Operación</Typography>

                            <Stack spacing={1.5} mt={1}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <SpeedIcon color="action" fontSize="small" />
                                    <Typography variant="body2"><strong>Kilometraje:</strong> {vehiculo.vehkmactual != null ? `${Number(vehiculo.vehkmactual).toFixed(1)} km` : 'N/R'}</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CalendarMonthIcon color="action" fontSize="small" />
                                    <Typography variant="body2"><strong>Año:</strong> {vehiculo.vehani || 'N/R'}</Typography>
                                </Box>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <AirlineSeatReclineNormalIcon color="action" fontSize="small" />
                                    <Typography variant="body2"><strong>Capacidad:</strong> {vehiculo.vehcap || 'N/R'} pasajeros</Typography>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Empresa / Propietario</Typography>
                            <Typography variant="body1" fontWeight={600} mt={1}>{vehiculo.orgnom || 'N/A'}</Typography>
                            <Typography variant="caption" color="text.secondary">{vehiculo.orgnomsoc || ''}</Typography>
                            {vehiculo.vehdes && (
                                <Typography variant="body2" color="text.secondary" mt={2} sx={{ fontStyle: 'italic' }}>
                                    "{vehiculo.vehdes}"
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* PROYECTOS ASOCIADOS */}
            <Typography variant="h6" gutterBottom>Proyectos Asociados</Typography>
            <Grid container spacing={2} mb={4}>
                {proyectos.map((proyecto) => {
                    const isSelected = selectedProject?.proid === proyecto.proid;
                    return (
                        <Grid item xs={12} sm={6} md={4} key={proyecto.proid}>
                            <Paper
                                onClick={() => handleOpenProject(proyecto)}
                                elevation={isSelected ? 6 : 1}
                                sx={{
                                    p: 2,
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
                                    backgroundColor: isSelected ? "#e3f2fd" : "#fff",
                                    transition: "all 0.2s ease-in-out",
                                    '&:hover': { boxShadow: 4, backgroundColor: "#f5f5f5" }
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <AssignmentIcon color={isSelected ? "primary" : "action"} />
                                    <Typography variant="subtitle1" fontWeight={600}>{proyecto.pronom}</Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* TABLA DE DOCUMENTOS */}
            {selectedProject && (
                <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>Documentos Requeridos – {selectedProject.pronom}</Typography>

                    <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
                        <Table>
                            <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                                <TableRow>
                                    <TableCell><strong>Tipo de Documento</strong></TableCell>
                                    <TableCell><strong>Fecha Emisión</strong></TableCell>
                                    <TableCell><strong>Fecha Vencimiento</strong></TableCell>
                                    <TableCell><strong>Vence En</strong></TableCell>
                                    <TableCell><strong>Estado</strong></TableCell>
                                    <TableCell align="center"><strong>Acciones</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentosRequeridos.map((docReq) => {
                                    const documentoVehiculo = documentosVehiculo.find(doc => doc.doctipid === docReq.doctipid);
                                    const estado = getEstadoDocumento(documentoVehiculo);

                                    return (
                                        <TableRow key={docReq.prodocreqid}>
                                            <TableCell>{docReq.doctip}</TableCell>
                                            <TableCell>{documentoVehiculo?.docfecemi ? format(new Date(documentoVehiculo.docfecemi), "dd/MM/yyyy") : "--"}</TableCell>
                                            <TableCell>{documentoVehiculo?.docfecven ? format(new Date(documentoVehiculo.docfecven), "dd/MM/yyyy") : "--"}</TableCell>
                                            <TableCell>
                                                {documentoVehiculo?.docest === "POR VENCER" && documentoVehiculo?.docfecven && (() => {
                                                    const today = new Date();
                                                    const vencimiento = new Date(documentoVehiculo.docfecven);
                                                    const diffDays = Math.ceil((vencimiento - today) / (1000 * 60 * 60 * 24));

                                                    let color = 'green', bgColor = '#e5fbe5', Icon = AccessTimeIcon;
                                                    if (diffDays < 20) { color = 'red'; bgColor = '#ffe5e5'; Icon = ErrorOutlineIcon; }
                                                    else if (diffDays < 40) { color = 'orange'; bgColor = '#fff4e5'; Icon = WarningIcon; }

                                                    return (
                                                        <Box display="inline-flex" alignItems="center" gap={0.5} px={1} py={0.5} borderRadius={2} sx={{ backgroundColor: bgColor, color: color, fontWeight: 'bold' }}>
                                                            <Icon fontSize="small" /><Typography variant="body2">{diffDays} días</Typography>
                                                        </Box>
                                                    );
                                                })()}
                                            </TableCell>
                                            <TableCell><Chip label={estado} color={estadoColor[estado]} size="small" /></TableCell>
                                            <TableCell align="center">
                                                <Box display="flex" justifyContent="center" gap={1}>
                                                    {documentoVehiculo?.docrut && (
                                                        <Tooltip title="Ver Documento">
                                                            <IconButton size="small" sx={{ color: "#607d8b" }} onClick={() => handleOpenPreview(documentoVehiculo.docrut)}>
                                                                <VisibilityIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {[administradorID, operationID].includes(user?.RolID) && (
                                                        documentoVehiculo ? (
                                                            <Tooltip title="Editar Documento">
                                                                <IconButton size="small" color="primary" onClick={() => handleOpenForm(documentoVehiculo, { lockDocumentType: true })}>
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Registrar Documento">
                                                                <IconButton size="small" color="primary" onClick={() => handleOpenForm({ vehid: parseInt(vehiculo.vehid), doctipid: docReq.doctipid, associationType: "vehiculo" }, { lockDocumentType: true })}>
                                                                    <AddIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* MODAL PREVIEW DOCUMENTO */}
            <Dialog open={openPreview} onClose={handleClosePreview} fullWidth maxWidth="xl">
                <DialogTitle>Vista Previa del Documento</DialogTitle>
                <DialogContent dividers>
                    {previewUrl?.endsWith(".pdf") ? (
                        <iframe src={previewUrl} width="100%" height="500px" style={{ border: "none" }} title="PDF Preview" />
                    ) : (
                        <img src={previewUrl} alt="Documento" style={{ width: "100%", maxHeight: "500px", objectFit: "contain" }} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview} color="secondary">Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* MODAL FORMULARIO DOCUMENTO */}
            {openForm && (
                <DocumentsForm
                    open={openForm}
                    handleClose={() => { setOpenForm(false); setEditDocument(null); }}
                    onSubmit={handleSubmitDocument}
                    defaultValues={formDefaultValues || {}}
                    config={formConfig}
                />
            )}

            {/* ALERTAS */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity} sx={{ width: "100%" }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default VehicleDetailDashboard;